// lk01 Sevgi kuni — lovers variant.
// A 1-year-anniversary guest page for a couple. Rose paper, wine accent,
// Pinyon Script as the display face. Hero leads with the couple's photo
// and a soft beating heart marker; below sit two sections you don't see
// elsewhere on the platform:
//
//   • "Yil raqamlarda" — four stat cards: kunlar birga, suratlar, sayohatlar,
//     "Sevaman"lar. Mock numbers, but a delightful frame for an anniversary.
//   • "12 oy" — a vertical month-by-month timeline of the year, one line
//     per month, with the wine accent threading through.
//
// No map (intimate, address irrelevant). With music. Reduced-motion safe.

import { useEffect, useState } from "react";
import type { VariantProps } from "./types";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useSound } from "@/lib/sound";

const STATS: Array<{ value: string; label: string }> = [
  { value: "365", label: "kun birga" },
  { value: "1284", label: "surat" },
  { value: "12", label: "sayohat" },
  { value: "∞", label: "\"Sevaman\"" },
];

const MONTHS: Array<{ month: string; line: string }> = [
  { month: "Fevral", line: "Birinchi marta — Toshkent restaurani, qor yog'ardi." },
  { month: "Mart", line: "Bahor — Chimganda piknik, sovuq, lekin issiq." },
  { month: "Aprel", line: "Birga ijara uy oldik. Birinchi tunni dasturxonsiz o'tkazdik." },
  { month: "May", line: "Bo'stonliq tog'lariga sayohat. Yulduzli tun." },
  { month: "Iyun", line: "Issiqxonadagi gullar — sevgan ranglarni tanlash." },
  { month: "Iyul", line: "Qora dengiz — birinchi xorij sayohati." },
  { month: "Avgust", line: "Toshkent issiqligi. Stol o'yinlar, kechki kino." },
  { month: "Sentabr", line: "Yangi ish, yangi loyiha — bir-birimizga suyandik." },
  { month: "Oktabr", line: "Samarqand — Registon, kuzgi yorug'lik." },
  { month: "Noyabr", line: "Birga oshpazlik kursi. Manti pishirishni o'rgandik." },
  { month: "Dekabr", line: "Birinchi qor — uyda, choy bilan." },
  { month: "Yanvar", line: "Yangi yil — birga rejalarimizni yozdik." },
];

export function LoversVariant({ t, isDark, ctx }: VariantProps) {
  const { play } = useSound();
  const [beat, setBeat] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    setBeat(true);
  }, []);

  const heroPhoto = t.photos[0];

  return (
    <div className="v-lk01">
      {/* HERO — couple photo + flowing names + beating heart */}
      <section
        className="hero-fill v-lk01-hero"
        style={{
          minHeight: "100vh",
          padding: "100px 24px 80px",
          background: `linear-gradient(160deg, ${t.bg} 0%, ${t.accent}1f 100%)`,
          color: t.ink,
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ maxWidth: 720, position: "relative", zIndex: 1 }}>
          {/* Beating heart above the photo */}
          <div
            aria-hidden
            style={{
              width: 36,
              height: 36,
              margin: "0 auto 20px",
              animation: beat ? "lk01-beat 1200ms ease-in-out infinite" : undefined,
            }}
          >
            <svg viewBox="0 0 32 32" style={{ width: "100%", height: "100%" }}>
              <path
                d="M 16 28 C 4 18 4 8 12 8 C 14 8 16 10 16 12 C 16 10 18 8 20 8 C 28 8 28 18 16 28 Z"
                fill={t.accent}
              />
            </svg>
          </div>

          {/* Couple photo — circular */}
          {heroPhoto && (
            <div
              className="v-lk01-hero-photo"
              role="img"
              aria-label="Couple photo"
              style={{
                width: "min(280px, 60vw)",
                aspectRatio: "1",
                margin: "0 auto 36px",
                borderRadius: "50%",
                overflow: "hidden",
                border: `2px solid ${t.accent}`,
                boxShadow: `0 18px 50px -16px ${t.ink}66`,
                backgroundImage: `url("${heroPhoto}")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          )}

          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.accent,
              marginBottom: 20,
            }}
          >
            ONE YEAR · 1 YIL BIRGA
          </div>

          <h1
            style={{
              fontFamily: t.font,
              fontWeight: 400,
              fontSize: "clamp(48px, 11vw, 110px)",
              lineHeight: 1,
              color: t.accent,
            }}
          >
            {t.names}
          </h1>

          <div
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 18,
              opacity: 0.75,
              marginTop: 18,
            }}
          >
            Sevgimizning birinchi yili
          </div>

          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.3em",
              marginTop: 24,
              opacity: 0.85,
            }}
          >
            {t.date.replace(/\./g, " · ")} · {t.venue.toUpperCase()}
          </div>
        </div>

        <style>{`
          @keyframes lk01-beat {
            0%   { transform: scale(1); }
            18%  { transform: scale(1.18); }
            30%  { transform: scale(1); }
            45%  { transform: scale(1.10); }
            60%  { transform: scale(1); }
            100% { transform: scale(1); }
          }
          .v-lk01 a:focus-visible,
          .v-lk01 button:focus-visible {
            outline: 2px solid ${t.accent};
            outline-offset: 2px;
          }
          @media (max-width: 768px) {
            .v-lk01-hero { padding: 64px 18px 48px !important; }
            .v-lk01-hero h1 { font-size: clamp(36px, 12vw, 96px) !important; }
            .v-lk01-hero-photo { width: min(60vw, 240px) !important; height: min(60vw, 240px) !important; }
            .v-lk01-section { padding: 64px 18px !important; }
            .v-lk01-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 14px !important; }
            .v-lk01 button, .v-lk01 a { min-height: 44px; }
          }
        `}</style>
      </section>

      <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />

      <StorySection
        t={t}
        eyebrow="Bizning hikoyamiz"
        title="Bir yil — yuz xil his"
        body="Bir yil oldin biz birga yashashga qaror qildik. Har kun yangi narsa, har oqshom yangi suhbat, har subh yangi rejalar. Bu kechqurun — o'sha birinchi qadamni eslash, va yangi yo'lga qadam qo'yish uchun. Sevgimiz hali boshlandi xolos."
      />

      {/* UNIQUE: stats */}
      <section
        className="v-lk01-section"
        style={{
          padding: "100px 24px",
          borderTop: `1px solid ${t.ink}22`,
          background: t.bg,
          color: t.ink,
        }}
      >
        <div style={{ maxWidth: 920, margin: "0 auto", textAlign: "center" }}>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.accent,
              marginBottom: 20,
            }}
          >
            YIL RAQAMLARDA
          </div>
          <h2
            style={{
              fontFamily: t.font,
              fontWeight: 400,
              fontSize: "clamp(36px, 6vw, 56px)",
              marginBottom: 56,
              color: t.accent,
            }}
          >
            Bir yil — to&apos;rt raqam
          </h2>
          <div
            className="v-lk01-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 18,
            }}
          >
            {STATS.map((s) => (
              <div
                key={s.label}
                style={{
                  padding: "32px 16px",
                  background: t.bg,
                  border: `1px solid ${t.accent}66`,
                  position: "relative",
                  boxShadow: `4px 5px 0 -1px ${t.accent}1f`,
                }}
              >
                <div
                  style={{
                    fontFamily: t.font,
                    fontSize: 48,
                    lineHeight: 1,
                    color: t.accent,
                    fontFeatureSettings: '"lnum" 1, "tnum" 1',
                  }}
                >
                  {s.value}
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.32em",
                    color: t.ink,
                    opacity: 0.7,
                    marginTop: 12,
                  }}
                >
                  {s.label.toUpperCase()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GallerySection t={t} />

      {/* UNIQUE: 12-month timeline */}
      <section
        className="v-lk01-section"
        style={{
          padding: "100px 24px",
          borderTop: `1px solid ${t.ink}22`,
          background: `${t.accent}10`,
          color: t.ink,
        }}
      >
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <div
            style={{
              textAlign: "center",
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.accent,
              marginBottom: 20,
            }}
          >
            12 OY
          </div>
          <h2
            style={{
              textAlign: "center",
              fontFamily: t.font,
              fontWeight: 400,
              fontSize: "clamp(36px, 6vw, 56px)",
              marginBottom: 56,
              color: t.accent,
            }}
          >
            Har oy — bir xotira
          </h2>
          <ol
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              position: "relative",
            }}
          >
            {/* Vertical wine spine */}
            <div
              aria-hidden
              style={{
                position: "absolute",
                left: 12,
                top: 8,
                bottom: 8,
                width: 1,
                background: t.accent,
                opacity: 0.5,
              }}
            />
            {MONTHS.map((m, i) => (
              <li
                key={m.month}
                style={{
                  display: "grid",
                  gridTemplateColumns: "26px 1fr",
                  gap: 18,
                  padding: "14px 0",
                  alignItems: "baseline",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: t.accent,
                    marginTop: 8,
                    marginLeft: 9,
                    boxShadow: `0 0 0 4px ${t.bg}`,
                  }}
                />
                <div>
                  <div
                    style={{
                      fontFamily: "var(--font-ui)",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.34em",
                      color: t.accent,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")} · {m.month.toUpperCase()}
                  </div>
                  <div
                    style={{
                      fontFamily: t.font,
                      fontStyle: "italic",
                      fontSize: 19,
                      lineHeight: 1.45,
                      marginTop: 4,
                    }}
                  >
                    {m.line}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <RsvpSection
        t={t}
        isDark={isDark}
        {...ctx.rsvp}
        label="Sevgini birga nishonlaymiz"
      />

      <ShareSection t={t} />

      <GuestFooter t={t} />
    </div>
  );
}
